import React, { useState, useEffect, useCallback } from 'react';
import { User, Company } from '../types';
import { apiService } from '../services/api';
import Modal from '../components/Modal';
import UserForm from '../components/UserForm';

const UsersManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [filterServer, setFilterServer] = useState<number | null>(null);
  const [filterCompany, setFilterCompany] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
  const [userRole, setUserRole] = useState<string>('user');
  const [servers, setServers] = useState<{id: number, name: string}[]>([]);
  const [companies, setCompanies] = useState<{id: number, name: string}[]>([]);

  const pageSize = 10;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;
      
      // Build query parameters
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: pageSize.toString(),
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (filterActive !== null) {
        params.append('is_active', filterActive.toString());
      }

      if (filterServer !== null) {
        params.append('server_id', filterServer.toString());
      }

      if (filterCompany !== null) {
        params.append('company_id', filterCompany.toString());
      }

      const response = await apiService.getUsers(currentPage, pageSize);
      
      if (response && typeof response === 'object' && 'items' in response) {
        // Paginated response
        setUsers(response.items || []);
      } else if (Array.isArray(response)) {
        // Direct array response
        setUsers(response);
      } else {
        // Fallback
        console.warn('getUsers: Unexpected response format:', response);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, filterActive, filterServer, filterCompany]);

  useEffect(() => {
    fetchUsers();
    fetchFiltersData();
    // Get user role from auth context or API
    // This is a placeholder - you'd get this from your auth system
    setUserRole('admin'); // For demo purposes
  }, [fetchUsers]);

  
  const fetchFiltersData = async () => {
    try {
      const [serversResponse, companiesResponse] = await Promise.all([
        apiService.getAllServers().catch(() => [] as any[]),
        apiService.getCompanies().catch(() => [] as any[])
      ]);
      setServers(Array.isArray(serversResponse) ? serversResponse.map((s: any) => ({ id: s.id, name: s.name })) : []);
      setCompanies(Array.isArray(companiesResponse) ? companiesResponse.map((c: any) => ({ id: c.id, name: c.name })) : []);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const handleCreate = () => {
    if (userRole !== 'admin' && userRole !== 'manager') {
      alert('Not enough permissions to create users');
      return;
    }
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEdit = (user: User) => {
    if (userRole !== 'admin' && userRole !== 'manager') {
      alert('Not enough permissions to edit users');
      return;
    }
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDelete = async (user: User) => {
    if (userRole !== 'admin' && userRole !== 'manager') {
      alert('Not enough permissions to delete users');
      return;
    }

    if (window.confirm(`Are you sure you want to delete user "${user.username}"?`)) {
      try {
        await apiService.deleteUser(user.id);
        fetchUsers();
      } catch (error: any) {
        console.error('Error deleting user:', error);
        
        let errorMessage = 'Cannot delete user.';
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        }
        
        alert(errorMessage);
      }
    }
  };

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      if (editingUser) {
        await apiService.updateUser(editingUser.id, data);
      } else {
        await apiService.createUser(data);
      }
      setShowModal(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Error saving user:', error);
      
      let errorMessage = 'An error occurred while saving the user.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    switch (filterType) {
      case 'active':
        setFilterActive(value === '' ? null : value === 'true');
        break;
      case 'server':
        setFilterServer(value === '' ? null : parseInt(value));
        break;
      case 'company':
        setFilterCompany(value === '' ? null : parseInt(value));
        break;
    }
    setCurrentPage(1);
  };

  const getServerName = (user: User) => {
    if (!user.server_assignments || user.server_assignments.length === 0) return '-';
    const serverNames = user.server_assignments
      .filter(assignment => assignment.is_active)
      .map(assignment => {
        const server = servers.find(s => s.id === assignment.server_id);
        return server ? server.name : `Server ${assignment.server_id}`;
      });
    return serverNames.length > 0 ? serverNames.join(', ') : '-';
  };

  const getCompanyName = (user: User) => {
    if (!user.company_assignments || user.company_assignments.length === 0) return '-';
    const companyNames = user.company_assignments
      .filter(assignment => assignment.is_active)
      .map(assignment => {
        const company = companies.find((c: {id: number, name: string}) => c.id === assignment.company_id);
        return company ? company.name : `Company ${assignment.company_id}`;
      });
    return companyNames.length > 0 ? companyNames.join(', ') : '-';
  };

  const canCreateUsers = userRole === 'admin' || userRole === 'manager';
  const canEditUsers = userRole === 'admin' || userRole === 'manager';
  const canDeleteUsers = userRole === 'admin' || userRole === 'manager';

  if (loading && currentPage === 1) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>User Management</h1>
        {canCreateUsers && (
          <button className="btn btn-primary" onClick={handleCreate}>
            + Add User
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={handleSearch}
            className="form-control"
          />
        </div>
        <div className="filter-group">
          <select
            value={filterActive === null ? '' : filterActive.toString()}
            onChange={(e) => handleFilterChange('active', e.target.value)}
            className="form-control"
          >
            <option value="">All Users</option>
            <option value="true">Active Only</option>
            <option value="false">Inactive Only</option>
          </select>
        </div>
        <div className="filter-group">
          <select
            value={filterServer === null ? '' : filterServer.toString()}
            onChange={(e) => handleFilterChange('server', e.target.value)}
            className="form-control"
          >
            <option value="">All Servers</option>
            {servers.map(server => (
              <option key={server.id} value={server.id}>
                {server.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <select
            value={filterCompany === null ? '' : filterCompany.toString()}
            onChange={(e) => handleFilterChange('company', e.target.value)}
            className="form-control"
          >
            <option value="">All Companies</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Server</th>
                <th>Company</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                    <h3 style={{ color: '#7f8c8d', marginBottom: '10px' }}>
                      {searchTerm || filterActive !== null || filterServer !== null || filterCompany !== null 
                        ? 'No Users Found' 
                        : 'No Users'
                      }
                    </h3>
                    <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
                      {searchTerm || filterActive !== null || filterServer !== null || filterCompany !== null
                        ? 'Try adjusting your search or filter criteria.'
                        : 'Start by adding your first user.'
                      }
                    </p>
                    {canCreateUsers && !searchTerm && filterActive === null && filterServer === null && filterCompany === null && (
                      <button className="btn btn-primary" onClick={handleCreate}>
                        Add First User
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.first_name} {user.last_name}</strong>
                      <div style={{ fontSize: '0.9em', color: '#7f8c8d' }}>
                        ID: {user.id}
                      </div>
                    </td>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`badge ${
                        user.access === 'admin' ? 'badge-danger' :
                        user.access === 'manager' ? 'badge-warning' :
                        'badge-info'
                      }`}>
                        {user.access || 'user'}
                      </span>
                    </td>
                    <td>{getServerName(user)}</td>
                    <td>{getCompanyName(user)}</td>
                    <td>
                      <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {user.locked && (
                        <span className="badge badge-warning" style={{ marginLeft: '5px' }}>
                          Locked
                        </span>
                      )}
                    </td>
                    <td>
                      {user.last_login 
                        ? new Date(user.last_login).toLocaleDateString()
                        : 'Never'
                      }
                    </td>
                    <td>
                      <div className="action-buttons">
                        {canEditUsers && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleEdit(user)}
                            title="Edit user"
                          >
                            Edit
                          </button>
                        )}
                        {canDeleteUsers && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(user)}
                            title="Delete user"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination would go here if implemented */}
      </div>

      {showModal && (
        <Modal
          title={editingUser ? 'Edit User' : 'Add User'}
          onClose={() => setShowModal(false)}
        >
          <UserForm
            initialData={editingUser}
            onSubmit={handleSubmit}
            onCancel={() => setShowModal(false)}
            isLoading={isSubmitting}
            currentUserRole={userRole}
          />
        </Modal>
      )}
    </div>
  );
};

export default UsersManagement;